import type {ReelData} from '../../types/types.ts';
import {NordjyskeContentAdapter} from '../adapters/nordjyske-content-adapter.ts';
import {muteIcon, unmuteIcon} from '../components/icons/reel-icons.ts';

export async function renderReelsView(reelsViewEl: HTMLElement, videoId?: string) {
	// TODO: Load adapter for client
	const contentAdapter = new NordjyskeContentAdapter();

	const reels: ReelData[] = await contentAdapter.loadReels();
	render();
	scrollToInitialReel(videoId);

	function render() {
		reelsViewEl.innerHTML = `
			<div class="menu">
				<a href="/home">Home</span></a>
			</div>
			<div class="reels-view">
				${reels
					.map((reel) => {
						const mobileSource = reel.sources.reduce((acc, curr) => {
							if (curr.target === 'mobile' && curr.width > acc.width) {
								return curr;
							}

							return acc;
						});
						const desktopSource = reel.sources.reduce((acc, curr) => {
							if (curr.target === 'desktop' && curr.width > acc.width) {
								return curr;
							}

							return acc;
						});
						return `
<div class="reel-card" data-reel-id="${reel.id}">
	<video poster="${reel.posterUrl}" muted loop playsinline>
		${reel.manifest ? `<source src="${reel.manifest.file}" type="${reel.manifest.type}">` : ''}
		<source src="${mobileSource.file}" type="${desktopSource.type}" media="(width <= 768px)">
		<source src="${desktopSource.file}" type="${desktopSource.type}" media="(width > 768px)">
	</video>
	<div class="reel-overlay">
		<div class="reel-meta">
			<span class="reel-tag">${reel.tags?.[0]?.displayName}</span>
			<span class="reel-published-date">${new Date(reel.publishedAt).toLocaleDateString()}</span>
		</div>
		<div class="reel-info">
			<div class="reel-controls">
				<button class="mute-button">${unmuteIcon}</button>
				<div class="progress-bar">
					<div class="progress"></div>
					<div class="progress-handle"></div>
				</div>
				<div class="time-display">00:00 / 00:00</div>
			</div>
			<div class="reel-text">
				<div class="reel-title">${reel.title}</div>
				<div class="reel-description">${reel.description}
						${
							reel.article
								? `
<div class="reel-article">
	<a href="${reel.article.url}">${reel.article.label}</a>
</div>`
								: ''
						}

				</div>
			</div>
		</div>
	</div>
</div>
				`;
					})
					.join('')}
			</div>
		`;
		setupVideoObserver();
		setupPlayerControls();
	}

	function scrollToInitialReel(reelId?: string) {
		if (!reelId) return;

		const reelCard = reelsViewEl.querySelector(`[data-reel-id="${reelId}"]`);
		if (reelCard) {
			reelCard.scrollIntoView();
		}
	}

	function setupPlayerControls() {
		// TODO: Set up and tear down as video enters/leaves viewport
		reelsViewEl.querySelectorAll('.reel-card').forEach((card) => {
			const video = card.querySelector('video') as HTMLVideoElement;
			const text = card.querySelector('.reel-text') as HTMLElement;
			const muteButton = card.querySelector('.mute-button') as HTMLButtonElement;
			const progressBar = card.querySelector('.progress-bar') as HTMLElement;
			const progress = card.querySelector('.progress') as HTMLElement;
			const progressHandle = card.querySelector('.progress-handle') as HTMLElement;
			const timeDisplay = card.querySelector('.time-display') as HTMLElement;
			let isDragging = false;

			// Tap to play/pause
			card.addEventListener('click', (event) => {
				if ((event.target as HTMLElement).closest('.reel-info')) return;
				if (video.paused) {
					video.play();
				} else {
					video.pause();
				}
			});

			// Mute/unmute
			text.addEventListener('click', (event) => {
				event.stopPropagation();
				text.classList.toggle('show-details');
			});
			// Mute/unmute
			muteButton.addEventListener('click', (event) => {
				event.stopPropagation();
				video.muted = !video.muted;
				muteButton.innerHTML = video.muted ? unmuteIcon : muteIcon;
			});

			// Progress bar and time display
			video.addEventListener('timeupdate', () => {
				if (isDragging) return;

				const progressBarRect = progressBar.getBoundingClientRect();

				const progressPercent = (video.currentTime / video.duration) * 100;
				progress.style.scale = `${progressPercent}% 100%`;
				progressHandle.style.translate = `${(progressPercent / 100) * progressBarRect.width}px 0`;

				const newTextContent = `${formatTime(video.currentTime)} / ${formatTime(video.duration)}`;
				if (timeDisplay.textContent !== newTextContent) {
					timeDisplay.textContent = newTextContent;
				}
			});

			video.addEventListener('loadedmetadata', () => {
				timeDisplay.textContent = `${formatTime(video.currentTime)} / ${formatTime(video.duration)}`;
			});

			// Scrubber
			const startDrag = (event: MouseEvent | TouchEvent) => {
				isDragging = true;
				card.classList.add('dragging');
				updateScrubber(event);
			};

			const stopDrag = () => {
				isDragging = false;
				card.classList.remove('dragging');
			};

			const updateScrubber = (event: MouseEvent | TouchEvent) => {
				if (!isDragging) return;
				const progressBarRect = progressBar.getBoundingClientRect();
				const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
				let newTime = ((clientX - progressBarRect.left) / progressBarRect.width) * video.duration;
				newTime = Math.max(0, Math.min(newTime, video.duration)); // Clamp between 0 and duration
				video.currentTime = newTime;
				const progressPercent = (newTime / video.duration) * 100;
				progress.style.scale = `${progressPercent}%`;
				progressHandle.style.translate = `${progressPercent}% 0`;
			};

			progressHandle.addEventListener('mousedown', startDrag);
			document.addEventListener('mousemove', updateScrubber);
			document.addEventListener('mouseup', stopDrag);

			progressHandle.addEventListener('touchstart', startDrag);
			document.addEventListener('touchmove', updateScrubber);
			document.addEventListener('touchend', stopDrag);

			progressBar.addEventListener('click', (event) => {
				const progressBarRect = progressBar.getBoundingClientRect();
				const clickPosition = event.clientX - progressBarRect.left;
				video.currentTime = (clickPosition / progressBarRect.width) * video.duration;
			});
		});
	}

	function setupVideoObserver() {
		const options = {
			root: reelsViewEl.parentElement,
			rootMargin: '0px',
			threshold: 0.8 // Trigger when 80% of the video is visible
		};

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				const video = entry.target as HTMLVideoElement;
				if (entry.isIntersecting) {
					// video.play().catch(() => {
					// 	// Autoplay was prevented.
					// });
					const reelCard = video.closest('.reel-card') as HTMLElement;
					if (reelCard) {
						const reelId = reelCard.dataset.reelId;
						if (reelId) {
							const newUrl = `/reels/${reelId}`;
							if (window.location.pathname !== newUrl) {
								history.replaceState(null, '', newUrl);
							}
						}
					}
				} else {
					video.pause();
				}
			});
		}, options);

		reelsViewEl.querySelectorAll('.reel-card video').forEach((video) => {
			observer.observe(video);
		});
	}

	function formatTime(time: number): string {
		const minutes = Math.floor(time / 60).toString();
		const seconds = Math.floor(time % 60)
			.toString()
			.padStart(2, '0');
		return `${minutes}:${seconds}`;
	}
}
